import { HttpRequestMethodTypes, JSON_CONTENT_TYPE_HEADER } from 'types';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { FetchOptions } from 'types/fetchWithRetry';

const getMessagesUrl = (conversationId: string) =>
  `/api/smartchat/conversations/${conversationId}/messages`;

// Role enum matching the protobuf definition
export enum Role {
  USER = 0,
  ASSISTANT = 1,
  SYSTEM = 2
}

interface MessageApiParams {
  jwt: string;
  conversationId: string;
}

interface CreateMessageParams extends MessageApiParams {
  content: string;
  role: Role;
  createdAt?: Date;
  metadata?: Record<string, string>;
}

interface ListMessagesParams extends MessageApiParams {
  limit?: number;
  cursor?: string;
  order?: 'asc' | 'desc';
}

interface DeleteMessageParams extends MessageApiParams {
  messageId: string;
}

interface UpdateMessageParams extends MessageApiParams {
  messageId: string;
  content?: string;
  metadata?: Record<string, string>;
}

export interface MessageResponse {
  messageId: string;
  conversationId: string;
  content: string;
  role: Role;
  metadata: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface ListMessagesResponse {
  messages: MessageResponse[];
  nextCursor?: string;
}

/**
 * Creates a new message in a conversation.
 */
export async function createMessage({
  jwt,
  conversationId,
  content,
  role,
  createdAt,
  metadata,
}: CreateMessageParams): Promise<MessageResponse | null> {
  const options: FetchOptions = {
    method: HttpRequestMethodTypes.POST,
    headers: {
      ...JSON_CONTENT_TYPE_HEADER,
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({
      content,
      role,
      createdAt: createdAt?.toISOString(),
      metadata,
    }),
  };

  const response = await fetchWithRetry(getMessagesUrl(conversationId), options);

  if (response.status === 404) {
    throw new Error('Conversation not found');
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to create message: ${response.status} - ${errorData.error}`);
  }

  const data = await response.json();
  return data.message;
}

/**
 * Lists messages in a conversation with pagination support.
 */
export async function listMessages({
  jwt,
  conversationId,
  limit = 50,
  cursor,
  order = 'desc',
}: ListMessagesParams): Promise<ListMessagesResponse> {
  const url = new URL(getMessagesUrl(conversationId), window.location.origin);
  url.searchParams.set('limit', limit.toString());
  url.searchParams.set('order', order);
  if (cursor) {
    url.searchParams.set('cursor', cursor);
  }

  const options: FetchOptions = {
    method: HttpRequestMethodTypes.GET,
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  };

  const response = await fetchWithRetry(url.pathname + url.search, options);

  if (response.status === 404) {
    throw new Error('Conversation not found');
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to list messages: ${response.status} - ${errorData.error}`);
  }

  return response.json();
}

/**
 * Gets all messages in a conversation by fetching all pages.
 */
export async function getAllMessages({
  jwt,
  conversationId,
  order = 'asc',
}: Omit<ListMessagesParams, 'limit' | 'cursor'>): Promise<MessageResponse[]> {
  const allMessages: MessageResponse[] = [];
  let cursor: string | undefined = undefined;

  do {
    const response = await listMessages({ jwt, conversationId, limit: 50, cursor, order });
    allMessages.push(...response.messages);
    cursor = response.nextCursor || undefined;
  } while (cursor);

  return allMessages;
}



/**
 * Updates a message.
 */
export async function updateMessage({
  jwt,
  conversationId,
  messageId,
  content,
  metadata,
}: UpdateMessageParams): Promise<MessageResponse> {
  const options: FetchOptions = {
    method: HttpRequestMethodTypes.PATCH,
    headers: {
      ...JSON_CONTENT_TYPE_HEADER,
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ content, metadata }),
  };

  const response = await fetchWithRetry(`${getMessagesUrl(conversationId)}/${messageId}`, options);

  if (response.status === 404) {
    throw new Error('Conversation or message not found');
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to update message: ${response.status} - ${errorData.error}`);
  }

  const data = await response.json();
  return data.message;
}

/**
 * Deletes a specific message by ID.
 */
export async function deleteMessage({
  jwt,
  conversationId,
  messageId,
}: DeleteMessageParams): Promise<void> {
  const options: FetchOptions = {
    method: HttpRequestMethodTypes.DELETE,
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  };

  const response = await fetchWithRetry(`${getMessagesUrl(conversationId)}/${messageId}`, options);

  if (response.status === 404) {
    throw new Error('Conversation or message not found');
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`Failed to delete message: ${response.status} - ${errorData.error}`);
  }
}
